import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Reply, Heart } from 'lucide-react';
import { apiUrl } from '../../utils/api';
import { CampaignComment } from '../../hooks/useCampaignDetail';
import { formatRelativeDate } from '../../utils/date';

interface Props {
  campaignId: string;
  comments: CampaignComment[];
  setComments: (comments: CampaignComment[]) => void;
  currentUser: any;
}

export function CommentSection({ campaignId, comments, setComments, currentUser }: Props) {
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/comments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ campaignId, content: newComment.trim(), parentCommentId: replyingTo }),
      });
      const data = await response.json();
      if (data.success) {
        const commentsResponse = await fetch(apiUrl(`/api/comments/campaign/${campaignId}`));
        const commentsData = await commentsResponse.json();
        if (commentsData.success) setComments(commentsData.data || []);
        setNewComment('');
        setReplyingTo(null);
      }
    } catch { /* ignore */ }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Foro Comunitario</h2>

      {currentUser ? (
        <form onSubmit={handleSubmit} className="mb-6">
          {replyingTo && (
            <div className="mb-2 text-sm text-gray-600 bg-gray-50 p-2 rounded flex items-center">
              <Reply className="w-3 h-3 mr-1" />
              Respondiendo a un comentario
              <button type="button" onClick={() => setReplyingTo(null)} className="ml-2 text-red-600 text-xs">Cancelar</button>
            </div>
          )}
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-medium">{currentUser.fullName?.charAt(0).toUpperCase() || 'U'}</span>
            </div>
            <div className="flex-1">
              <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                placeholder="Comparte un mensaje de apoyo..." rows={3} maxLength={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-400">{newComment.length}/1000</span>
                <button type="submit" disabled={!newComment.trim() || isSubmitting}
                  className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-1">
                  <Send className="w-3 h-3" />
                  <span>{replyingTo ? 'Responder' : 'Comentar'}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-gray-600 text-sm mb-2">Inicia sesión para participar</p>
          <button onClick={() => navigate('/login')} className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700">
            Iniciar Sesión
          </button>
        </div>
      )}

      {comments.length === 0 ? (
        <div className="text-center py-8">
          <MessageCircle className="mx-auto h-10 w-10 text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No hay comentarios aún. Sé el primero.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-100 pb-4 last:border-b-0">
              <div className="flex items-start space-x-3">
                <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {(comment?.user as any)?.profileImageUrl ? (
                    <img src={(comment.user as any).profileImageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gray-600 text-xs font-medium">{comment?.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{comment?.user?.fullName || 'Usuario'}</span>
                    {(comment?.user as any)?.title && (
                      <span className="text-xs text-gray-500">· {(comment.user as any).title}</span>
                    )}
                    {comment.isDonor && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                        <Heart className="w-2 h-2 mr-0.5" />
                        Donante
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{formatRelativeDate(comment?.createdAt)}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{comment?.content}</p>
                  {currentUser && (
                    <button onClick={() => setReplyingTo(comment?.id)} className="mt-1 text-xs text-indigo-600 hover:underline flex items-center">
                      <Reply className="w-3 h-3 mr-0.5" />Responder
                    </button>
                  )}
                  {comment?.replies && comment.replies.length > 0 && (
                    <div className="mt-3 ml-4 space-y-3">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex items-start space-x-2">
                          <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {(reply?.user as any)?.profileImageUrl ? (
                              <img src={(reply.user as any).profileImageUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-gray-500 text-xs">{reply?.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}</span>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center space-x-1">
                              <span className="font-medium text-xs text-gray-900">{reply?.user?.fullName || 'Usuario'}</span>
                              {(reply?.user as any)?.title && (
                                <span className="text-[10px] text-gray-500">· {(reply.user as any).title}</span>
                              )}
                              <span className="text-xs text-gray-400">{formatRelativeDate(reply?.createdAt)}</span>
                            </div>
                            <p className="text-xs text-gray-700 mt-0.5">{reply?.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
